import {Paper, Box, Typography, Button, Alert} from '@mui/material'
import {useNavigate, useLocation} from 'react-router-dom'
import {useFormik} from 'formik'
import * as yup from 'yup'
import CircularProgress from '@mui/material/CircularProgress'
import {DigitInput} from '../../../components/inputs/DigitInput'
import {useCallback} from 'react'
import {actions as authActions} from '../AuthRedux'
import {useDispatch} from 'react-redux'
import * as authAPI from '../AuthApi'
import {isAxiosError} from 'axios'

export const MfaPage = () => {
  const dispatch = useDispatch()
  const {state} = useLocation()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, {setStatus}) => {
      setStatus('')
      try {
        const {data} = await authAPI.validateMfa(values.mfa, state.token)
        dispatch(authActions.setMfaAuth(data))
        if (data.user.isFirstLogin) {
          navigate('/auth/reset-password', {replace: true})
        } else {
          navigate('/', {replace: true})
        }
      } catch (e) {
        let message = 'Unknown error has occurred, please try again later.'
        if (isAxiosError<{error?: string}>(e)) {
          const errorMessage = e.response?.data?.error
          if (errorMessage) {
            message = errorMessage
          }
        } else if (e instanceof Error) {
          message = e.message
        }
        setStatus(message)
      }
    },
  })

  const handleDigitInputChange = useCallback(
    (value: string) => {
      formik.setFieldValue('mfa', value)
    },
    [formik]
  )

  if (!state) {
    navigate('/auth/login', {
      replace: true,
    })
  }

  return (
    <Paper
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        overflow: 'hidden',
        width: '80vw',
        maxWidth: '30rem',
      }}
    >
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          textAlign: 'center',
          textTransform: 'uppercase',
          p: theme.spacing(2),
        })}
      >
        <Typography
          component='h1'
          variant='h6'
          sx={{
            letterSpacing: '0.1em',
            fontWeight: '700',
          }}
        >
          Enter OTP
        </Typography>
        <Typography variant='caption'>Use your preferred 2FA app to get your OTP</Typography>
      </Box>
      <Box
        component='form'
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing(5),
          [theme.breakpoints.down('md')]: {
            padding: theme.spacing(3),
          },
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
          },
        })}
        autoComplete='off'
        onSubmit={formik.handleSubmit}
      >
        {formik.status && (
          <Alert severity='error' sx={(theme) => ({mb: theme.spacing(3)})}>
            {formik.status}
          </Alert>
        )}
        <Box
          component={DigitInput}
          length={6}
          value={formik.values.mfa}
          onChange={handleDigitInputChange}
          sx={(theme) => ({mb: theme.spacing(3)})}
          autoFocus
          id='field-mfa'
        />
        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
          <Button
            type='button'
            variant='contained'
            color='primary'
            onClick={() => navigate(-1)}
            sx={(theme) => ({mr: theme.spacing(1)})}
            id='button-back'
          >
            Back
          </Button>
          <Button
            disabled={formik.isSubmitting || !formik.isValid}
            type='submit'
            variant='contained'
            color='primary'
            id='button-submit'
            sx={{
              position: 'relative',
            }}
          >
            Log In
            {formik.isSubmitting && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}

const initialValues = {
  mfa: '',
}

const validationSchema = yup.object().shape({
  mfa: yup.string().required('Please enter your username.'),
})

export default MfaPage
